import { jsx } from 'react/jsx-runtime';
import { useBrand } from '@shared-ui/brand-context';
import { useExperiment } from '@shared-ui/experiment-context';
import { c as classNames, u as useEGDSContext } from '../theme-provider/index-chunk.esm.js';
import styles from '../styles/uitk-egds-icons.css';
import { v as validatePropType } from '../validate-prop-types/index-chunk.esm.js';

/* Type theme */
/** ******************* */
const EGDSIconThemeMap = [
    ['positive', 'uitk-icon-positive-theme'],
    ['negative', 'uitk-icon-negative-theme'],
    ['primary', 'uitk-icon-primary-theme'],
    ['secondary', 'uitk-icon-secondary-theme'],
    ['inverse', 'uitk-icon-inverse-theme'],
    ['emphasis', 'uitk-icon-emphasis-theme'],
    ['default', 'uitk-icon-default-theme'],
];
const EGDSIconThemes = new Map(EGDSIconThemeMap);
const EGDSIconSpotlightMap = [
    ['info', 'spotlight-container-info'],
    ['standard', 'spotlight-container-standard'],
    ['positive', 'spotlight-container-positive'],
    ['warning', 'spotlight-container-warning'],
];
new Map(EGDSIconSpotlightMap);

const getClassNameList = classNames.bind(styles);
var EGDSIconSize;
(function (EGDSIconSize) {
    EGDSIconSize["EXTRA_SMALL"] = "xsmall";
    EGDSIconSize["SMALL"] = "small";
    EGDSIconSize["MEDIUM"] = "medium";
    EGDSIconSize["LARGE"] = "large";
    EGDSIconSize["XLARGE"] = "xlarge";
})(EGDSIconSize || (EGDSIconSize = {}));
const EGDSIcon = (props) => {
    const baseCssClass = 'uitk-icon';
    const throwNameError = (nameError) => {
        typeof window !== 'undefined' && console.error(`Invalid icon name prop: ${nameError}`);
    };
    const { className, description, id, name, size = EGDSIconSize.MEDIUM, theme, title, shadow, spotlight } = props;
    const { data: contextData } = useEGDSContext();
    const { contextClassNames } = contextData ?? {};
    if (!name) {
        throwNameError(name);
    }
    if (description && !id && typeof window !== 'undefined') {
        console.error(`Icon ${name} requires 'id' if 'description' is provided`);
    }
    const dropShadowValues = {
        dx: 0,
        dy: 0,
        stdDeviation: 1,
    };
    const uid = Math.random().toString(36).substring(7);
    const filterId = `shadow-filter${id ? `-${id}` : `-${uid}`}`;
    const domRef = (b) => {
    };
    const isDirectional = name && /_(?:back|forward|left|right|before|next|half|send)$/.test(name);
    // If spotlight isn't using the UitkIconSpotlightInterface, restructure it
    // to make working with the spotlight prop more predictable
    const spotLightIsObject = typeof spotlight === 'object';
    let newSpotlight = false;
    if (spotLightIsObject) {
        newSpotlight = spotlight;
    }
    else if (spotlight) {
        newSpotlight = {
            theme: spotlight,
            background: true,
        };
    }
    const wrapperClassNames = getClassNameList(baseCssClass, className, contextClassNames?.EGDSIcon, {
        [`${baseCssClass}-directional`]: isDirectional,
        [`${baseCssClass}-${size}`]: validatePropType(size, EGDSIconSize) && !spotlight && size !== EGDSIconSize.MEDIUM,
        [`${baseCssClass}-spotlight-icon`]: newSpotlight,
    }, theme && !spotlight && EGDSIconThemes.get(theme));
    const spotlightIconContainerClassNames = getClassNameList(newSpotlight && [
        `${baseCssClass}-spotlight`,
        `${baseCssClass}-spotlight-${newSpotlight.theme}`,
        newSpotlight.background && `${baseCssClass}-spotlight-has-background`,
    ]);
    const isSVGFEDropShadowElement = typeof SVGFEDropShadowElement === 'function';
    const style = shadow && isSVGFEDropShadowElement ? { filter: `url(#${filterId})` } : undefined;
    const { getIconPath, getIconsHref } = useBrand();
    const pathToRender = (svgName) => {
        const iconToRender = svgName?.indexOf('icon__') === 0 ? svgName.replace('icon__', '') : svgName;
        const iconPathString = getIconPath?.(iconToRender);
        iconPathString || throwNameError(svgName);
        return iconPathString || '';
    };
    const svgNodes = [
        title && `<title>${title}</title>`,
        description && `<desc id="${id}-description">${description}</desc>`,
        shadow &&
            `<defs>
      <filter id="${filterId}">
        <feDropShadow dx="${dropShadowValues.dx}" dy="${dropShadowValues.dy}" stdDeviation="${dropShadowValues.stdDeviation}"/>
      </filter>
    </defs>`,
    ]
        .filter(Boolean)
        .join('');
    const a11yProps = {
        'aria-describedby': description ? `${id}-description` : undefined,
        'aria-hidden': title || description ? undefined : true,
        'aria-label': title || undefined,
        role: title || description ? 'img' : undefined,
    };
    const iconProps = {
        viewBox: '0 0 24 24',
        xmlns: 'http://www.w3.org/2000/svg',
        xmlnsXlink: 'http://www.w3.org/1999/xlink',
    };
    const svgProps = {
        ref: domRef,
        className: wrapperClassNames,
        style,
        ...a11yProps,
        ...iconProps,
    };
    const innerNodes = (html) => {
        if (html.length > 0) {
            return {
                dangerouslySetInnerHTML: {
                    __html: html,
                },
            };
        }
        return {};
    };
    const { exposure } = useExperiment('Bernie_SVG_delivery', true);
    let innerNodesMarkup = '';
    if (exposure?.bucket === 1) {
        innerNodesMarkup = `${svgNodes}<use href="#${name}" />`;
    }
    else if (exposure?.bucket === 2) {
        const iconsHref = `${getIconsHref?.(name)}`;
        innerNodesMarkup = `${svgNodes}<use href="${iconsHref}" />`;
    }
    else {
        innerNodesMarkup = `${svgNodes}${pathToRender(name)}`;
    }
    const iconMarkup = jsx("svg", { ...svgProps, ...innerNodes(innerNodesMarkup) });
    return spotlight ? jsx("div", { className: spotlightIconContainerClassNames, children: iconMarkup }) : iconMarkup;
};
const getMetaData = (metaDataProps) => {
    if (!metaDataProps) {
        console.error('Must pass something');
        return false;
    }
    if (!metaDataProps.data) {
        console.error('Must pass name or data property');
        return false;
    }
    const icon = metaDataProps;
    const iconData = icon.data;
    const iconProps = { ...metaDataProps };
    // Override attributes with custom ones
    const svgProps = {
        role: 'icon',
        height: '24',
        viewBox: '0 0 24 24',
        width: '24',
        xmlns: 'http://www.w3.org/2000/svg',
        ...icon.attrs,
    };
    Object.assign(svgProps, {
        'aria-hidden': true,
    });
    return {
        iconData,
        iconProps,
        svgProps,
    };
};
const attributize = (obj) => Object.keys(obj)
    // eslint-disable-next-line
    .reduce((str, attr) => (str += `${attr}="${obj[attr]}" `), '')
    .trim();
const getMarkerPopup = (markerProps) => {
    const { svgProps, iconData } = getMetaData(markerProps);
    return `<svg ${attributize(svgProps)}>${iconData}</svg>`;
};
EGDSIcon.displayName = 'EGDSIcon';

export { EGDSIconSize as E, EGDSIcon as a, attributize as b, getMarkerPopup as c, getMetaData as g };
//# sourceMappingURL=index-chunk.esm.js.map
